import { useState } from 'react'
import { Box, Flex, Grid, Input, Textarea, Spinner } from '@chakra-ui/react'
import { useFormColors, FormLabel, FileInput, DatePicker } from '@minetrace/ui/forms'
import { Modal, toaster, useT } from '@minetrace/ui'
import { EXPLOSIVE_AUTHORIZATION_TYPES } from '../../../data/enums'
import type { AddExplosiveAuthorizationRequest } from '../../../api/models/request/proveedor'
import type { ExplosiveAuthorizationResponse } from '../../../api/models/response/proveedor'

const TYPE_ACQUISITION = 1
const TYPE_STORAGE = 2

interface AutorizacionModalProps {
  open: boolean
  onClose: () => void
  miningRightName?: string
  initial?: ExplosiveAuthorizationResponse | null
  onSave: (data: AddExplosiveAuthorizationRequest, documentFile?: File) => Promise<void>
}

export default function AutorizacionModal({ open, onClose, miningRightName, initial, onSave }: AutorizacionModalProps) {
  const c = useFormColors()
  const { t } = useT()

  const [authorizationType, setAuthorizationType] = useState<number>(initial?.authorizationType ?? 0)
  const [resolutionNumber, setResolutionNumber] = useState(initial?.resolutionNumber ?? '')
  const [emissionDate, setEmissionDate] = useState(initial?.emissionDate ?? '')
  const [expirationDate, setExpirationDate] = useState(initial?.expirationDate ?? '')
  const [authorizedExplosives, setAuthorizedExplosives] = useState(initial?.authorizedExplosives ?? '')
  const [maxCapacity, setMaxCapacity] = useState(initial?.maxCapacity ?? '')
  const [documentFile, setDocumentFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)

  const inputStyles = {
    bg: c.bgInput, border: 'none', borderRadius: 'xl', color: c.text,
    _placeholder: { color: c.textMuted }, _focus: { outline: 'none', ring: '1px', ringColor: c.accent },
  } as const

  const selectStyles: React.CSSProperties = {
    width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.75rem',
    border: 'none', fontSize: '0.875rem', background: c.bgInput, color: c.text, outline: 'none',
  }

  const handleSave = async () => {
    if (!authorizationType) { toaster.error({ description: t('proveedores.wizard.step7.authTypeRequired') }); return }
    if (!emissionDate) { toaster.error({ description: t('proveedores.wizard.step7.authEmissionRequired') }); return }

    const data: AddExplosiveAuthorizationRequest = {
      authorizationType,
      resolutionNumber: resolutionNumber.trim() || undefined,
      emissionDate,
      expirationDate: expirationDate || undefined,
      authorizedExplosives: authorizationType === TYPE_ACQUISITION ? (authorizedExplosives.trim() || undefined) : undefined,
      maxCapacity: authorizationType === TYPE_STORAGE ? (maxCapacity.trim() || undefined) : undefined,
    }
    setSaving(true)
    try {
      await onSave(data, documentFile ?? undefined)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const today = new Date().toISOString().split('T')[0]

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? t('proveedores.wizard.step7.authModalTitleEdit') : t('proveedores.wizard.step7.authModalTitleNew')}
      subtitle={miningRightName}
      size="lg"
      footer={
        <Flex gap={3} justifyContent="flex-end">
          <Box as="button" type="button" px={4} py={2} borderRadius="lg" border="1px solid" borderColor={c.border} color={c.text} fontWeight="600" fontSize="sm" cursor="pointer" _hover={{ bg: c.bgHover }} onClick={onClose}>
            {t('common.cancel')}
          </Box>
          <Box as="button" type="button" px={5} py={2} borderRadius="lg" bg={c.accent} color="white" fontWeight="600" fontSize="sm" cursor="pointer" opacity={saving ? 0.6 : 1} _hover={{ opacity: 0.9 }} onClick={handleSave}>
            {saving ? <Spinner size="sm" color="white" /> : initial ? t('proveedores.wizard.step7.authUpdate') : t('proveedores.wizard.step7.authRegister')}
          </Box>
        </Flex>
      }
    >
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
        <Box>
          <FormLabel required>{t('proveedores.wizard.step7.authType')}</FormLabel>
          <select value={authorizationType} onChange={(e) => setAuthorizationType(Number(e.target.value))} style={selectStyles}>
            <option value={0}>{t('proveedores.wizard.step7.authTypePlaceholder')}</option>
            {EXPLOSIVE_AUTHORIZATION_TYPES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Box>
        <Box>
          <FormLabel required>{t('proveedores.wizard.step7.authEmissionDate')}</FormLabel>
          <DatePicker maxDate={today} value={emissionDate} onChange={setEmissionDate} />
        </Box>
        <Box>
          <FormLabel>{t('proveedores.wizard.step7.authResolution')}</FormLabel>
          <Input value={resolutionNumber} onChange={(e) => setResolutionNumber(e.target.value.slice(0, 100))} placeholder={t('proveedores.wizard.step7.authResolutionPlaceholder')} {...inputStyles} />
        </Box>
        <Box>
          <FormLabel>{t('proveedores.wizard.step7.authExpirationDate')}</FormLabel>
          <DatePicker minDate={emissionDate || today} value={expirationDate} onChange={setExpirationDate} />
        </Box>
      </Grid>

      {/* Campo condicional según el tipo */}
      {authorizationType === TYPE_ACQUISITION && (
        <Box mt={4}>
          <FormLabel>{t('proveedores.wizard.step7.authExplosives')}</FormLabel>
          <Textarea value={authorizedExplosives} onChange={(e) => setAuthorizedExplosives(e.target.value.slice(0, 1000))} placeholder={t('proveedores.wizard.step7.authExplosivesPlaceholder')} {...inputStyles} />
        </Box>
      )}
      {authorizationType === TYPE_STORAGE && (
        <Box mt={4}>
          <FormLabel>{t('proveedores.wizard.step7.authMaxCapacity')}</FormLabel>
          <Input value={maxCapacity} onChange={(e) => setMaxCapacity(e.target.value.slice(0, 500))} placeholder={t('proveedores.wizard.step7.authMaxCapacityPlaceholder')} {...inputStyles} />
        </Box>
      )}

      <Box mt={4}>
        <FileInput
          label={t('proveedores.wizard.step7.authDocument')}
          accept=".pdf"
          maxSizeMb={5}
          value={documentFile}
          onChange={setDocumentFile}
          existingFileName={initial?.documentFile?.originalFileName ?? undefined}
        />
      </Box>
    </Modal>
  )
}
